import React, { useState } from "react";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorsCompleteness as Completeness } from "@/features/observability/types/frontend-errors.types";

type Props = {
    completeness: Completeness;
    compact: boolean;
};

type Tone = "gaps" | "clear" | "unknown";

const VIEW: Record<Tone, { label: string; note: string }> = {
    gaps: {
        label: "El navegador ha declarado huecos",
        note: "Zonas rayadas en la gráfica: un valle ahí no significa que no hubiera errores.",
    },
    clear: {
        label: "Sin huecos declarados",
        note: "Ningún navegador ha informado de huecos en el periodo. No significa que el dato sea exacto.",
    },
    unknown: {
        label: "Desconocido",
        note: "La fuente no informa de completitud para este periodo.",
    },
};

const toneOf = (completeness: Completeness): Tone => {
    if (completeness.status === "gaps_reported") return "gaps";
    if (completeness.status === "no_gaps_reported") return "clear";
    return "unknown";
};

export function FrontendErrorsCompleteness({ completeness, compact }: Props) {
    const [open, setOpen] = useState(true);
    const tone = toneOf(completeness);
    const view = VIEW[tone];
    const gaps = completeness.status === "gaps_reported" ? completeness.gaps : [];
    const truncated = completeness.status === "gaps_reported" && completeness.gapsTruncated;

    return (
        <div className="fe-card fe-comp" data-testid="fe-completeness" data-tone={tone}>
            <div className="fe-card__hd">
                <div className="fe-card__title">Completitud</div>
                {!compact && gaps.length > 0 ? (
                    <button type="button" className="fe-link" aria-expanded={open} onClick={() => setOpen(!open)}>
                        {open ? `Ocultar ${gaps.length} huecos` : `Ver ${gaps.length} huecos`}
                        <span className="material-symbols-outlined">{open ? "expand_less" : "expand_more"}</span>
                    </button>
                ) : null}
            </div>
            <div className="fe-comp__state">
                <span className="fe-comp__dot" />
                <span className="fe-comp__label">{view.label}</span>
                {tone === "clear" ? (
                    <span
                        className="material-symbols-outlined fe-info"
                        title="No significa exacto: solo que ningún navegador ha declarado huecos."
                        aria-label="No significa exacto: solo que ningún navegador ha declarado huecos."
                    >
                        info
                    </span>
                ) : null}
            </div>
            {compact ? null : <div className="fe-comp__note">{view.note}</div>}
            {!compact && open && gaps.length > 0 ? (
                <ul className="fe-gaps">
                    {gaps.map((gap) => (
                        <li key={`${gap.from}-${gap.to}-${gap.reason}-${gap.status}`} className="fe-gap">
                            <span className="fe-gap__range">
                                {`${frontendErrorsFormat.dateTime(gap.from)} – ${frontendErrorsFormat.dateTime(gap.to)}`}
                            </span>
                            <span className="fe-gap__devices">{`${frontendErrorsFormat.count(gap.devices)} disp.`}</span>
                            <span className="fe-gap__reason">
                                {frontendErrorsFormat.gapReason(gap.reason)} · <span className="fe-muted">{gap.reason}</span>
                            </span>
                            <span className="fe-gap__status">{gap.status}</span>
                        </li>
                    ))}
                </ul>
            ) : null}
            {!compact && truncated ? (
                <div className="fe-gaps__more">
                    <span className="material-symbols-outlined">more_horiz</span>
                    Hay más huecos de los que se muestran (máx. 50)
                </div>
            ) : null}
        </div>
    );
}
